"use client";
import React, { useState } from "react";
import Link from "next/link";
import QuestionMark from "../icons/questionMark";

const MembershipPlans: React.FC = () => {
  const [selectedPlan, setSelectedPlan] = useState("Pro"); // Default selected plan
  const [tooltipPlan, setTooltipPlan] = useState<string | null>(null); // Tooltip visibility for specific plan

  const plans = [
    {
      name: "Basic",
      price: "$250/week",
      description:
        "Kickstart your online presence with the Basic package, offering a professional website and comprehensive filling services. It's the perfect foundation for small businesses or startups looking for an affordable way to get online quickly. Gain reliability and simplicity without overcomplicating your digital needs.",
    },
    {
      name: "Pro",
      price: "$400/week",
      description:
        "Looking to grow your reach? The Pro package builds on the Basic plan with additional services like SEO Core and website maintenance. Enjoy better search engine rankings and ongoing support to ensure your site performs at its best. Ideal for businesses aiming to improve visibility and efficiency.",
    },
    {
      name: "Ultra",
      price: "$750/week",
      description:
        "Take your business to the next level with the Ultra package. It includes everything in the Pro plan plus advanced marketing strategies, free board system integration, and modern frameworks for scalability. Enhance your brand's credibility and customer engagement with tailored solutions for a competitive edge.",
    },
    {
      name: "Max",
      price: "$1000/week",
      description:
        "Achieve ultimate digital dominance with the Max package. This all-inclusive plan features mobile app development, graphic design, a professional brandbook, and more. From streamlined frameworks to marketing mastery, Max empowers your business with unparalleled tools for success. Your brand deserves nothing less.",
    },
  ];

  return (
    <section className="membership-plans flex flex-col justify-center items-center gap-8 md:px-4 py-6">
      <div className="flex flex-col w-full justify-center xl:pl-[25px] xl:pr-[25px] xl:flex-row 2xl:max-w-screen-2xl">
        <div className="xl:border-r xl:mb-0 xl:pr-[25px] border-b border-black mb-8 pt-4 pb-[25px] xl:border-b-0">
          <h2 className="2xl:text-3xl flex justify-center text-center md:text-left pb-4 text-2xl font-[500] xl:max-w-[250px] 2xl:max-w-[500px]">
            Subscriptions
          </h2>
          <Link
            href="/subscriptions"
            className="upperTitle flex justify-center text-[#3b82f6] md:justify-start items-center gap-1 group"
          >
            Read More
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 25"
              fill="none"
              className="transform group-hover:translate-x-1 transition-transform duration-300"
            >
              <path
                d="M4 12.7598L20 12.7598M20 12.7598L14 18.7598M20 12.7598L14 6.75977"
                stroke="#3b82f6"
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
            </svg>
          </Link>
        </div>

        <div className="group w-full flex flex-col justify-center items-center gap-[35px] md:grid md:grid-cols-2 xl:pl-[25px] xl:flex xl:flex-row">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`plan-card justify-between text-left p-4 flex flex-col items-start h-full w-full flex-grow transition-colors rounded-[25px] duration-500 ${
                selectedPlan === plan.name ? "bg-blue-50" : "bg-white"
              }`}
              onMouseEnter={() => setSelectedPlan(plan.name)}
              onMouseLeave={() => setSelectedPlan("Pro")}
            >
              <div className="flex items-center justify-center md:justify-start md:items-start flex-col w-full">
                <h3 className="text-2xl">{plan.name}</h3>
                <div className="flex items-center gap-2">
                  <p className="text-lg text-center">{plan.price}</p>
                  <div
                    onMouseEnter={() => setTooltipPlan(plan.name)}
                    onMouseLeave={() => setTooltipPlan(null)}
                    className="relative"
                  >
                    <QuestionMark />
                    {tooltipPlan === plan.name && (
                      <div className="absolute left-6 top-0 bg-white p-2 border border-gray-200 rounded shadow-lg text-sm w-48">
                        Minimum subscription term is 6 months.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-base mt-4 mb-4">{plan.description}</p>
              <Link
                className="w-full border border-blue-500 rounded-full"
                href="/contact"
              >
                <button
                  className={`w-full flex justify-center items-center gap-2.5 py-2 px-5 rounded-full border text-blue-500 transition duration-300 ${
                    selectedPlan === plan.name
                      ? "bg-blue-500 text-white"
                      : "bg-blue-50 hover:bg-blue-500 hover:text-white"
                  }`}
                >
                  Subscribe
                </button>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MembershipPlans;
